import { api, type Json, type Session } from "../api.js";
import {
  entries,
  failure,
  identifier,
  Notice,
  ObjectTable,
  ReasonAction,
  text,
  useData,
} from "../shared/ui.js";

export function AdminPage({ session }: { session: Session }) {
  const principals = useData(() => api.principals(), []);
  const meta = (reason: string): Json => ({
    reason,
    expectedAuthorizationRevision: session.principal.authorizationRevision,
    idempotencyKey: identifier(),
  });

  return (
    <section
      className="control-page administration-page unified-operations-control-page"
      data-page-layout="fiori-floorplan-security"
    >
      {principals.error !== undefined && (
        <Notice error>
          {failure(principals.error)}{" "}
          <button onClick={principals.refresh}>Retry</button>
        </Notice>
      )}
      <h2 id="access-security-title" className="sr-only">
        Access &amp; security
      </h2>

      <ObjectTable
        title="Observed identities"
        description="Identities seen by PayDay after Authentik admission. This is not a local access list."
        rows={entries(principals.value, "principals")}
        columns={[
          "displayName",
          "enabled",
          "authorizationRevision",
          "sessionRevision",
          "id",
        ]}
        columnLabels={{
          displayName: "Identity",
          enabled: "Observed",
          authorizationRevision: "Access version",
          sessionRevision: "Session version",
          id: "PayDay ID",
        }}
        emptyMessage="No Authentik-admitted identities have been observed yet."
      />

      <section className="control-card principal-management">
        <div className="section-heading">
          <div>
            <h2>End active sessions</h2>
          </div>
        </div>
        {entries(principals.value, "principals").map((principal) => (
          <details key={String(principal["id"])}>
            <summary>
              <span>{String(principal["displayName"] ?? principal["id"])}</span>
              <small>
                Session version {text(principal["sessionRevision"])}
              </small>
            </summary>
            <ReasonAction
              label="Sign out everywhere"
              description="Ends current PayDay sessions; the person can sign in again if Authentik admits them."
              submit={(reason) =>
                api
                  .principal(String(principal["id"]), "revoke-sessions", {
                    ...meta(reason),
                    expectedAuthorizationRevision: Number(
                      principal["authorizationRevision"],
                    ),
                  })
                  .then(principals.refresh)
              }
            />
          </details>
        ))}
      </section>
    </section>
  );
}
